import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Server } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useNotification } from '@/components/notifications';
import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import {
  listMcpServers,
  McpServer,
  provisionGenericMcpServer,
} from '@/services/mcp-servers';
import { useOrgStore } from '@/stores/org-store';
import { orgPath } from '@/utils/org-path';

import { CreateMcpServerWizard } from './create-mcp-server-wizard';

const statusVariant = (status: string) =>
  status === 'active' ? 'secondary' : 'destructive';

export const McpServersPage = () => {
  const { currentOrg } = useOrgStore();
  const navigate = useNavigate();
  const notification = useNotification();
  const notificationRef = useRef(notification);
  notificationRef.current = notification;
  const [servers, setServers] = useState<McpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!currentOrg?.orgid) return;
    setLoading(true);
    try {
      const response = await listMcpServers(currentOrg.orgid);
      if (response.detail) {
        notificationRef.current.error(response.detail);
        return;
      }
      let list = response.data?.servers || [];
      // The generic org gateway is auto-provisioned: one per org, created
      // the first time an admin opens this page (idempotent server-side).
      if (!list.some((server) => server.server_type === 'generic')) {
        const provisioned = await provisionGenericMcpServer(currentOrg.orgid);
        if (provisioned.data?.server) {
          list = [provisioned.data.server, ...list];
        } else if (provisioned.detail) {
          notificationRef.current.error(
            `Org gateway provisioning failed: ${provisioned.detail}`,
          );
        }
      }
      setServers(list);
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.orgid]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className='w-full'>
      <div className='mb-6 flex flex-wrap items-start justify-between gap-3'>
        <div>
          <PageTitle title='MCP Servers' />
          <PageDescription>
            MCP endpoints exposing this organization&apos;s workflows, agents,
            and data assets to external tools (Claude, Cursor, Snowflake,
            Databricks): the auto-provisioned org gateway (everything, via
            meta-tools) plus curated servers you compose. Each server has its
            own credentials, usage docs, and audit ledger. Org admins only.
          </PageDescription>
        </div>
        <Button onClick={() => setWizardOpen(true)} data-testid='mcp-create'>
          <Plus className='size-4' /> New MCP server
        </Button>
      </div>

      {loading ? (
        <div className='flex items-center gap-2 py-12 text-muted-foreground'>
          <Loader2 className='size-5 animate-spin' /> Loading MCP servers…
        </div>
      ) : servers.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center gap-3 py-12 text-center text-muted-foreground'>
            <Server className='size-8' />
            <p>
              No MCP servers yet. Create one to expose selected workflows and
              data to external MCP clients — or ask the platform chat to
              &ldquo;make me an MCP server&rdquo;.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className='flex flex-col gap-3'>
          {servers.map((server) => (
            <Card
              key={server.server_id}
              className='cursor-pointer transition-colors hover:bg-muted/40'
              data-testid={`mcp-server-${server.slug}`}
              onClick={() =>
                navigate(orgPath(`/settings/mcp-servers/${server.server_id}`))
              }
            >
              <CardContent className='flex flex-col gap-2 pt-6'>
                <div className='flex flex-wrap items-center gap-2'>
                  <h2 className='font-medium'>{server.name}</h2>
                  <Badge variant={statusVariant(server.status)}>
                    {server.status}
                  </Badge>
                  {server.server_type === 'generic' ? (
                    <Badge variant='outline'>
                      org gateway · auto-provisioned
                    </Badge>
                  ) : (
                    <Badge variant='outline'>{server.server_type}</Badge>
                  )}
                  {server.origin === 'chat' && (
                    <Badge variant='outline'>created via chat</Badge>
                  )}
                  <span className='ml-auto text-sm text-muted-foreground'>
                    {server.server_type === 'generic'
                      ? 'meta-tools'
                      : `${server.tool_count} tool${server.tool_count === 1 ? '' : 's'}`}
                  </span>
                </div>
                {server.server_type === 'generic' && (
                  <p className='text-xs text-amber-600 dark:text-amber-500'>
                    Org-scoped service identity — per-user scoping coming with
                    ENG-5281.
                  </p>
                )}
                {server.description && (
                  <p className='text-sm text-muted-foreground'>
                    {server.description}
                  </p>
                )}
                <div
                  className='flex items-center gap-1 text-xs text-muted-foreground'
                  onClick={(event) => event.stopPropagation()}
                >
                  <code className='rounded bg-muted px-1.5 py-0.5'>
                    {server.endpoint}
                  </code>
                  <CopyButton text={server.endpoint} size='small' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateMcpServerWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        orgId={currentOrg?.orgid || ''}
        onCreated={load}
      />
    </div>
  );
};
